import { TransactionDetail } from "@/type/core/transaction/transaction.types";
import BasicTable from "@components/core/tables/BasicTable";
import SeeMoreTableWrapper from "@components/core/tables/SeeMoreTableWrapper";
import ChevronIcon from "@static/core/icons/icon-chevron.svg?react";
import { Document } from "@type/core/dashboard/dashboard.types";
import { columns } from "@views/core/extranet/transactions/details/columns/TransactionDocumentsColumns";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import InfoModal from "@components/core/modals/InfoModal";

interface TransactionDocumentsSectionProps {
  title: string;
  authorized: boolean;
  transaction: TransactionDetail;
  documents: Document[];
  showDownload: boolean;
  isAttachmentsPanel?: boolean;
  className?: string;
}

const TransactionDocumentsSection: FunctionComponent<
  TransactionDocumentsSectionProps
> = ({
  title,
  authorized,
  transaction,
  documents,
  showDownload,
  isAttachmentsPanel = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const hasNotSigned =
    transaction &&
    !isAttachmentsPanel &&
    transaction.status === "finished" &&
    documents.length > 0 &&
    !documents
      .map((document) =>
        document.signatures.some((signature) => signature.status === "signed"),
      )
      .reduce((acc, cur) => acc && cur, true);
  const anySigned = documents.some((doc) =>
    doc.signatures.some((signature) => signature.status === "signed"),
  );
  const isNotSignedByEveryone = documents.some((doc) => {
    const signatureEmails = new Set(
      doc.signatures.map((signature) => signature.email),
    );
    const documentSignatories = transaction.signatories?.filter((signatory) =>
      signatureEmails.has(signatory.email),
    );
    return documentSignatories?.length !== transaction.signatories?.length;
  });

  return (
    <div className={`app-section-transaction-documents ${className}`}>
      <div className="p-24">
        <h2 className="d-flex align-items-center text-black" data-test={title}>
          <ChevronIcon className="rotate-270 text-primary icon-30" />
          {title}
        </h2>
      </div>
      <div className="px-24 pb-24">
        {hasNotSigned && (
          <div className="pb-48 text-danger pl-48">
            {t("process.scellement.errorScellementDocuments")}
          </div>
        )}
        {transaction && transaction.sealMoved && (
          <div className="pb-48 text-danger pl-48">
            {t("process.scellement.sealMoved")}
          </div>
        )}
        <SeeMoreTableWrapper
          disableMaxHeight={true}
          id={
            isAttachmentsPanel
              ? "detailAttachmentsTable"
              : "detailDocumentsTable"
          }
          showWrapper={documents.length > 3}
        >
          <div data-test={title}>
            <BasicTable
              id={
                isAttachmentsPanel
                  ? "detailAttachmentsTable"
                  : "detailDocumentsTable"
              }
              pagination={false}
              columns={columns(
                transaction,
                anySigned,
                authorized,
                showDownload,
                hasNotSigned,
                isAttachmentsPanel,
              )}
              dataSource={documents}
              rowKey="id"
              className="table-no-header table-light-grey"
            />
            {isNotSignedByEveryone && !isAttachmentsPanel && (
              <div className="text-warning text-right mt-15">
                {t("process.signature.requirementModeSomeActive")}
                <InfoModal value={"combinedSignature"} info />
              </div>
            )}
          </div>
        </SeeMoreTableWrapper>
      </div>
    </div>
  );
};

export default TransactionDocumentsSection;
